import axios from 'axios'

import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'

import {
  ApiSuccessResponse,
  CancelSubscriptionRequest,
  CheckoutResponse,
  DeletePaymentMethodRequest,
  InvoicesResponse,
  PaymentMethodsResponse,
  PortalResponse,
  SetDefaultPaymentMethodRequest,
  SetupIntentResponse,
  SubscriptionPricesResponse,
  SubscriptionStatusResponse,
} from './interfaces/subscriptionApi.interface'

const getAuthHeaders = () => ({
  'Access-Control-Allow-Origin': ANALYTICS_API_URL,
  Vary: 'Origin',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

// Prices (public)
export const getSubscriptionPrices =
  async (): Promise<SubscriptionPricesResponse> => {
    const { data } = await axios.get<SubscriptionPricesResponse>(
      `${ANALYTICS_API_URL}/v1/subscription/prices`
    )
    return data
  }

// Status (private)
export const getSubscriptionStatus =
  async (): Promise<SubscriptionStatusResponse> => {
    const { data } = await axios.get<SubscriptionStatusResponse>(
      `${ANALYTICS_API_URL}/v1_private/subscription/status`,
      { headers: getAuthHeaders() }
    )
    return data
  }

// Checkout & Portal
export const createCheckoutSession = async (
  priceId: string,
  promoCode?: string
): Promise<CheckoutResponse> => {
  const { data } = await axios.post<CheckoutResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/checkout`,
    promoCode ? { priceId, promoCode } : { priceId },
    { headers: getAuthHeaders() }
  )
  return data
}

export const createPortalSession = async (): Promise<PortalResponse> => {
  const { data } = await axios.post<PortalResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/portal`,
    {},
    { headers: getAuthHeaders() }
  )
  return data
}

// Payment Methods
export const getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
  const { data } = await axios.get<PaymentMethodsResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/payment-methods`,
    { headers: getAuthHeaders() }
  )
  return data
}

export const createSetupIntent = async (): Promise<SetupIntentResponse> => {
  const { data } = await axios.post<SetupIntentResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/setup-intent`,
    {},
    { headers: getAuthHeaders() }
  )
  return data
}

export const setDefaultPaymentMethod = async (
  payload: SetDefaultPaymentMethodRequest
): Promise<ApiSuccessResponse> => {
  const { data } = await axios.post<ApiSuccessResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/payment-methods/default`,
    payload,
    { headers: getAuthHeaders() }
  )
  return data
}

export const deletePaymentMethod = async (
  payload: DeletePaymentMethodRequest
): Promise<ApiSuccessResponse> => {
  const { data } = await axios.delete<ApiSuccessResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/payment-methods`,
    { data: payload, headers: getAuthHeaders() }
  )
  return data
}

// Invoices
export const getInvoices = async (): Promise<InvoicesResponse> => {
  const { data } = await axios.get<InvoicesResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/invoices`,
    { headers: getAuthHeaders() }
  )
  return data
}

// Cancel/Resume
export const cancelSubscription = async (
  payload: CancelSubscriptionRequest
): Promise<ApiSuccessResponse> => {
  const { data } = await axios.post<ApiSuccessResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/cancel`,
    payload,
    { headers: getAuthHeaders() }
  )
  return data
}

export const resumeSubscription = async (): Promise<ApiSuccessResponse> => {
  const { data } = await axios.post<ApiSuccessResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/resume`,
    {},
    { headers: getAuthHeaders() }
  )
  return data
}

// Accept a custom plan offered by the admin → returns a Checkout URL to redirect to.
export const acceptOffer = async (): Promise<CheckoutResponse> => {
  const { data } = await axios.post<CheckoutResponse>(
    `${ANALYTICS_API_URL}/v1_private/subscription/accept-offer`,
    {},
    { headers: getAuthHeaders() }
  )
  return data
}
